import React from 'react';
import { Box, Container, IconButton, Typography } from '@mui/material';
import SliderWrapper from '../../Components/SliderWrapper';
import type { Settings } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { PortfolioDetailsNormalized } from '../../Pages/Portfolio/PortfolioDetails/PortfolioData';
import { useNavigate } from 'react-router-dom';

type Img = { src: string; alt: string };

type PortfolioItem = {
    id: string;
    title: string;
    image: Img; // ensure we carry both src and alt
    category: string;
};

// NOTE: use normalized data so every image has {src, alt}
const portfolioItems: PortfolioItem[] = PortfolioDetailsNormalized.map(
    (item: any) => ({
        id: item.id,
        category: item.breadcrumb.current,
        title: item.heading,
        image:
            typeof (item as any).mainImage === 'string'
                ? {
                    src: item.mainImage as unknown as string,
                    alt:
                        (item.heading || item.breadcrumb?.current || item.id || 'Image') +
                        ' – main image',
                }
                : (item.mainImage as Img),
    }),
);

// Custom arrows (kept even though arrows are disabled, exactly like the reference)
const ArrowLeft = ({ onClick }: any) => (
    <IconButton
        onClick={onClick}
        sx={{
            position: 'absolute',
            top: '50%',
            left: -50,
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: '#fff',
            '&:hover': { backgroundColor: '#ddd' },
        }}
    >
        <ArrowBackIosNewIcon />
    </IconButton>
);

const ArrowRight = ({ onClick }: any) => (
    <IconButton
        onClick={onClick}
        sx={{
            position: 'absolute',
            top: '50%',
            right: -50,
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: '#fff',
            '&:hover': { backgroundColor: '#ddd' },
        }}
    >
        <ArrowForwardIosIcon />
    </IconButton>
);

const settings: Settings = {
    dots: true,
    arrows: false, // same as reference
    infinite: true,
    speed: 700,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: false,
    customPaging: () => (
        <Box
            sx={{
                width: 10,
                height: 10,
                backgroundColor: '#fff',
                borderRadius: '50%',
                display: 'inline-block',
                mx: 0.5,
            }}
        />
    ),
    appendDots: (dots: any) => (
        <Box
            component="ul"
            sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 3,
                listStyle: 'none',
                '& li.slick-active div': {
                    backgroundColor: '#000', // Active dot color
                },
            }}
        >
            {dots}
        </Box>
    ),
    responsive: [
        {
            breakpoint: 900,
            settings: {
                slidesToShow: 1,
            },
        },
    ],
};

const formatUrl = (id: string): string => id.toLowerCase().replace(/\s+/g, '-');

const BAR_WIDTH = 35; // px

const PortfolioSlider: React.FC = () => {
    const navigate = useNavigate();
    return (
        <Container maxWidth='lg'>

            <Box sx={{ px: { xs: 0, md: 4 }, position: 'relative' }}>
                <Typography
                    variant="h3"
                    textAlign="center"
                    sx={{
                        
                        letterSpacing: "4px",
                        mb: 5,
                        color: 'white'
                    }}
                >
                    LET ME SHOW YOU 
                </Typography>

                <SliderWrapper {...settings}>
                    {portfolioItems.map((item, index) => (
                        <Box key={index}>
                            <Box
                                onClick={() => navigate(`/portfolio/${formatUrl(item.id)}`)}
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    height: 500,
                                    transition: 'transform 0.7s ease',
                                    '&:hover .overlay': { opacity: 1, visibility: 'visible' },
                                    '&:hover .overlay::before': { height: '100%' },
                                    transform: 'scaleX(1)',
                                }}
                            >
                                {/* IMAGE WRAPPER */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        height: '100%',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* fixed-width blue ribbon with HORIZONTAL label (rotated -90°) */}
                                    <Box
                                        aria-hidden
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: `${BAR_WIDTH}px`,
                                            height: '100%',
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            zIndex: 9,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {/* center the text then rotate so it reads horizontally along the label */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%) rotate(-90deg)',
                                                transformOrigin: 'center',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#0f63a5',
                                                    fontWeight: 800,
                                                    letterSpacing: '0.12em',
                                                    lineHeight: 1,
                                                    p: 0,
                                                    m: 0,
                                                    display: 'inline-block',
                                                    fontSize: { xs: 11, md: 12 }, // keep small so it fits inside BAR_WIDTH
                                                    userSelect: 'none',
                                                }}
                                            >
                                                {item.category}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Image */}
                                    <img
                                        src={item.image.src}
                                        alt={item.image.alt || item.title || item.category || item.id}
                                        loading="lazy"
                                        decoding="async"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            display: 'block',
                                        }}
                                    />

                                    {/* Overlay */}
                                    <Box
                                        className="overlay"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            opacity: 0,
                                            visibility: 'hidden',
                                            transition: '0.5s ease',
                                            zIndex: 8,
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                transition: '0.5s ease',
                                                zIndex: 1,
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                backgroundColor: '#fff',
                                                color: '#0f63a5',
                                                textDecoration: 'none',
                                                borderRadius: '50%',
                                                fontWeight: 700,
                                                textAlign: 'center',
                                                width: 120,
                                                height: 120,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                marginLeft: '40px',
                                                zIndex: 9,
                                            }}
                                        >
                                            Know More
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </SliderWrapper>
            </Box>
        </Container>
    );
};

export default PortfolioSlider;
